import { defineStore } from 'pinia'
import { useUserStore } from '@/authentication/user.store'
import { ACTION_TYPES, CONTENT_TYPES, actionPermissions, blurContentPermissions } from '@/util/access_visibility'
import {
  deleteAllPresentLevelStudentStatementsForSkill,
  postPresentLevelStudentStatement,
  putPresentLevelStudentStatementSelectStatus,
  returnGradeFromEnrolledGrade
} from '@/presentlevels/api_v1.js'
import { buildGoalQueriesAndRecommendations, skillNamesBySubject } from '@/presentlevels/no-ai/goal_recommendations'
import type { DraftedSkills, GoalRecommendation, SkillNamesBySubject, StatementsByArea } from '@/presentlevels/no-ai/goal_recommendations'
import { getRelevantGoalsForQueries } from '@/presentlevels/no-ai/relevant_goals_api'
import type { ActionPermissions } from '@/components/limit_action_copy'
import type { SaveStudentStatementRequest, StudentStatement } from '@/presentlevels/no-ai/skill_statement_types'
import type { Resource } from '@/resources/resource_types'

export type BreadcrumbsInfo = {
  uuid: string
  availableStages: string[]
}

export type StudentInfo = {
  studentName: string
  enrolledGrade: string
  areasOfNeed: string[]
}

export type BlurPermissions = {
  blur: boolean
}

export type AlignedAssessment = Resource & {
  grades?: string[]
  subjects?: string[]
}

export type Conclusion = {
  statement: string | null
  resourceDocs: AlignedAssessment[]
}

type SavedStatementResponse = {
  statement?: StudentStatement
}

const emptyDraftedSkills = (): DraftedSkills => ({ strengths: {}, needs: {} })

export const usePresentLevelsWizardStore = defineStore('presentLevelsWizard', {
  state: () => ({
    conclusionStatement: null as string | null,
    resourceDocs: [] as AlignedAssessment[],
    isSaving: false,
    lastUpdate: null as Date | string | null,
    isBlockerShown: false,
    blockerPermissions: null as ActionPermissions | null,

    goalRecommendations: [] as GoalRecommendation[],
    isLoadingRecommendations: false,
    areRecommendationsRetrieved: false,
    hasGoalRecommendationsFailed: false,
    gradeForBrowse: '',
    draftedSkills: emptyDraftedSkills(),
    relevantGoalsRequest: 0,
    studentName: '',
    enrolledGrade: '',
    areasOfNeed: [] as string[],
    breadcrumbsInfo: { uuid: '', availableStages: [] } as BreadcrumbsInfo,
    hasAccess: null as boolean | null
  }),
  getters: {
    developPermissions(): ActionPermissions {
      return actionPermissions(ACTION_TYPES.DEVELOP_PLW, '', {}, useUserStore().authInfo)
    },
    blurPermissions(): BlurPermissions {
      return blurContentPermissions(CONTENT_TYPES.PRESENTLEVEL, {}, useUserStore().authInfo)
    },
    draftedStrengths(): SkillNamesBySubject {
      return this.draftedSkills.strengths
    }
  },
  actions: {
    setStudentInfo({ studentName, enrolledGrade, areasOfNeed }: StudentInfo) {
      this.studentName = studentName
      this.enrolledGrade = enrolledGrade
      this.areasOfNeed = areasOfNeed
    },
    setBreadcrumbsInfo(info: BreadcrumbsInfo) {
      this.breadcrumbsInfo = info
    },
    setHasAccess(isAllowed: boolean) {
      this.hasAccess = isAllowed
    },
    resetStudentInfo() {
      this.setStudentInfo({ studentName: '', enrolledGrade: '', areasOfNeed: [] })
      this.setBreadcrumbsInfo({ uuid: '', availableStages: [] })
    },
    showBlocker(permissions: ActionPermissions) {
      this.blockerPermissions = permissions
      this.isBlockerShown = true
    },
    blockIfDevelopingIsNotAllowed(): boolean {
      if (!this.developPermissions.block) {
        return false
      }
      this.showBlocker(this.developPermissions)
      return true
    },
    async saveStudentStatement(request: SaveStudentStatementRequest): Promise<StudentStatement | undefined> {
      this.isSaving = true
      try {
        const response: SavedStatementResponse = await postPresentLevelStudentStatement({
          uuid: request.presentLevelUuid,
          statementUuid: request.statementUuid,
          subject: request.subject,
          skillLevel: '',
          skillUuid: request.skillUuid,
          statementText: request.statementText,
          defaultStatement: request.defaultStatement,
          multiSelect: request.isMultiSelect
        })
        if (response?.statement) {
          this.lastUpdate = new Date()
        }
        return response?.statement
      } catch (error) {
        console.error(error)
        return undefined
      } finally {
        this.isSaving = false
      }
    },
    async deselectStudentStatement(presentLevelUuid: string, statementUuid: string) {
      this.isSaving = true
      try {
        await putPresentLevelStudentStatementSelectStatus(presentLevelUuid, statementUuid, false)
        this.lastUpdate = new Date()
      } catch (error) {
        console.error(error)
      } finally {
        this.isSaving = false
      }
    },
    async deleteAllStudentStatementsForSkill(presentLevelUuid: string, skillUuid: string): Promise<boolean> {
      this.isSaving = true
      try {
        await deleteAllPresentLevelStudentStatementsForSkill(presentLevelUuid, skillUuid)
        this.lastUpdate = new Date()
        return true
      } catch (error) {
        console.error(error)
        return false
      } finally {
        this.isSaving = false
      }
    },
    setConclusion({ statement, resourceDocs }: Conclusion) {
      this.conclusionStatement = statement
      this.resourceDocs = resourceDocs
    },
    setDraftedSkillsFromStatements(statementsByArea: StatementsByArea) {
      this.draftedSkills = {
        strengths: skillNamesBySubject(statementsByArea.strengths),
        needs: skillNamesBySubject(statementsByArea.needs)
      }
    },
    resetDraftedSkills() {
      this.draftedSkills = emptyDraftedSkills()
    },
    resetGoalRecommendations() {
      this.relevantGoalsRequest++
      this.goalRecommendations = []
      this.isLoadingRecommendations = false
      this.areRecommendationsRetrieved = false
      this.hasGoalRecommendationsFailed = false
      this.gradeForBrowse = ''
    },
    async retrieveRelevantGoals({ statementsByArea, grade }: { statementsByArea: StatementsByArea, grade: string }) {
      const request = ++this.relevantGoalsRequest
      this.gradeForBrowse = returnGradeFromEnrolledGrade(grade)
      const { queries, recommendations } = buildGoalQueriesAndRecommendations(statementsByArea, this.gradeForBrowse)
      // Set before the request so the page can outline what is about to load.
      this.goalRecommendations = recommendations
      this.isLoadingRecommendations = true
      this.hasGoalRecommendationsFailed = false
      this.areRecommendationsRetrieved = false
      const goalsPerQuery = await getRelevantGoalsForQueries(queries).catch((error: unknown) => {
        console.error('Error retrieving relevant goals: ', error)
        return null
      })
      if (request !== this.relevantGoalsRequest) {
        return
      }
      if (goalsPerQuery) {
        this.goalRecommendations = recommendations.map((recommendation, index) => ({ ...recommendation, goals: goalsPerQuery[index] }))
      } else {
        this.hasGoalRecommendationsFailed = queries.length > 0
        this.goalRecommendations = []
      }
      this.isLoadingRecommendations = false
      this.areRecommendationsRetrieved = true
    }
  }
})
